import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Trophy, BarChart3, Megaphone, Users, Check, Rocket } from 'lucide-react';
import api from '../services/api';
import { parseJsonArray } from '../utils/parseJsonArray';
import './AnnouncementPopup.css';

const AnnouncementPopup = () => {
  const [visible, setVisible] = useState(false);
  const [popup, setPopup] = useState(null);
  const navigate = useNavigate();
  const timerRef = React.useRef(null);

  useEffect(() => {
    fetchPopup();
    
    // Clean up timer
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const fetchPopup = async () => {
    try {
      const response = await api.get('/popup/active');
      if (response.data.success && response.data.data.popup) {
        const popupData = response.data.data.popup;
        
        // If showOnce is set and already closed, don't show
        if (popupData.showOnce) {
          const popupStorageKey = `popup_closed_${popupData.id}`;
          const hasBeenClosed = localStorage.getItem(popupStorageKey);
          if (hasBeenClosed) {
            console.log('Popup already closed (showOnce mode), will not show again');
            return;
          }
        }

        setPopup(popupData);
        
        // Start timer to show popup
        startPopupTimer(popupData);
      } else {
        console.log('No popup data retrieved, may be disabled or not exist');
      }
    } catch (error) {
      console.error('Failed to fetch popup:', error);
    }
  };

  const startPopupTimer = (popupData) => {
    const delay = (popupData.delaySeconds || 5) * 1000;
    
    const showPopup = () => {
      setVisible(true);
      console.log(`Popup displayed (delayed ${delay / 1000} seconds)`);
    };

    // Start first timer immediately
    timerRef.current = setTimeout(showPopup, delay);
  };

  // Restart timer after popup is closed
  const scheduleNextPopup = () => {
    // Clear previous timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    if (popup && !popup.showOnce) {
      const delay = (popup.delaySeconds || 5) * 1000;
      console.log(`Popup will show again in ${delay / 1000} seconds`);
      timerRef.current = setTimeout(() => {
        setVisible(true);
        console.log('Popup shown again');
      }, delay);
    }
  };

  const handleClose = () => {
    setVisible(false);
    
    // Mark as closed in localStorage (only if showOnce is enabled)
    if (popup?.showOnce) {
      localStorage.setItem(`popup_closed_${popup.id}`, 'true');
      // If showOnce is set, clear timer and don't show again
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    } else {
      // If showOnce is not set, schedule next display after closing
      scheduleNextPopup();
    }
  };

  const handleButtonClick = () => {
    setVisible(false);
    
    // Mark as closed in localStorage (only if showOnce is enabled)
    if (popup?.showOnce) {
      localStorage.setItem(`popup_closed_${popup.id}`, 'true');
      // If showOnce is set, clear timer and don't show again
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    } else {
      // If showOnce is not set, schedule next display after button click
      scheduleNextPopup();
    }
    
    const link = popup?.buttonLink;
    console.log('Button clicked, navigating to:', link);
    
    if (link) {
      if (link.startsWith('http')) {
        window.open(link, '_blank');
      } else {
        navigate(link);
      }
    }
  };

  if (!visible || !popup) return null;

  const features = parseJsonArray(popup.features);

  return (
    <div className="popup-overlay" onClick={handleClose}>
      <div className="popup-container" onClick={(e) => e.stopPropagation()}>
        <button className="popup-close" onClick={handleClose}>
          <X size={20} />
        </button>
        
        <div className="popup-header">
          <h1 className="popup-title">{popup.title}</h1>
          {popup.subtitle && <p className="popup-subtitle">{popup.subtitle}</p>}
        </div>

        {(popup.totalProfit || popup.monthlyProfit || popup.activeTrades) && (
          <div className="popup-stats">
            {popup.totalProfit && (
              <div className="popup-stat">
                <div className="popup-stat-icon">
                  <Trophy size={24} />
                </div>
                <div className="popup-stat-content">
                  <span className="popup-stat-value">{popup.totalProfit}</span>
                  <span className="popup-stat-label">Total Profit</span>
                </div>
              </div>
            )}
            {popup.monthlyProfit && (
              <div className="popup-stat">
                <div className="popup-stat-icon">
                  <BarChart3 size={24} />
                </div>
                <div className="popup-stat-content">
                  <span className="popup-stat-value">{popup.monthlyProfit}</span>
                  <span className="popup-stat-label">Monthly Profit</span>
                </div>
              </div>
            )}
            {popup.activeTrades !== undefined && (
              <div className="popup-stat">
                <div className="popup-stat-icon">
                  <BarChart3 size={24} />
                </div>
                <div className="popup-stat-content">
                  <span className="popup-stat-value">{popup.activeTrades}</span>
                  <span className="popup-stat-label">Active Trades</span>
                </div>
              </div>
            )}
          </div>
        )}

        {popup.announcementTitle && (
          <div className="popup-announcement">
            <div className="popup-announcement-header">
              <Megaphone size={20} className="popup-announcement-icon" />
              <span className="popup-announcement-title">{popup.announcementTitle}</span>
            </div>
            {popup.announcementSubtitle && (
              <p className="popup-announcement-subtitle">{popup.announcementSubtitle}</p>
            )}
            {popup.announcementContent && (
              <p className="popup-announcement-content">{popup.announcementContent}</p>
            )}
          </div>
        )}

        {features.length > 0 && (
          <div className="popup-features">
            <div className="popup-features-header">
              <Users size={20} />
              <span>Exclusive Benefits</span>
            </div>
            <div className="popup-features-grid">
              {features.map((feature, index) => (
                <div key={index} className="popup-feature">
                  <Check size={18} className="popup-feature-check" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <button className="popup-button" onClick={handleButtonClick}>
          <Rocket size={20} />
          <span>{popup.buttonText || 'Get Started'}</span>
        </button>
      </div>
    </div>
  );
};

export default AnnouncementPopup;
